import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { type ReactNode } from 'react'
import { BrowserRouter } from 'react-router-dom'

import { ErrorBoundary } from '@/components/common/ErrorBoundary'
import { ToastProvider } from '@/components/ui/Toast'
import { AdminSessionProvider } from '@/features/auth/AdminSessionProvider'
import { AppRoutes } from '@/routes'

/**
 * Admin defaults: data goes stale quickly because moderators act on it, and a
 * failed query retries once rather than hammering an endpoint that is down.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: true,
    },
    mutations: { retry: 0 },
  },
})

/**
 * PROVIDER ORDER MATTERS, AND NOT ARBITRARILY.
 *
 *   ErrorBoundary        catches a throw from anything below it, the session
 *                        bootstrap included.
 *   QueryClientProvider  above the session, because signing OUT has to be able
 *                        to clear the query cache — an operator who signs out
 *                        and hands the laptop over must not leave the next
 *                        person a cache full of user rows.
 *   ToastProvider        above the routes, so a write failure on any screen has
 *                        somewhere to render.
 *   BrowserRouter        above the session provider, because the route guard and
 *                        the provider must share one router context — a
 *                        `<Navigate>` decided by the session is a router
 *                        operation.
 *   AdminSessionProvider wraps every route, including `/login`: the login page
 *                        itself needs `signIn`, and needs to know when a session
 *                        already exists so it can get out of the way.
 */
export function App(): ReactNode {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <BrowserRouter>
            <AdminSessionProvider>
              <AppRoutes />
            </AdminSessionProvider>
          </BrowserRouter>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  )
}
